import React, { useCallback, useEffect, useState } from 'react';
import { Form, Row, Col, Button, Badge, Modal, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import useEmployeeIds from './useEmployeeIds';
import Swal from 'sweetalert2';
import {
    getAdvanceRequests,
    getAdvanceReasons,
    approveAdvanceRequest,
    rejectAdvanceRequest,
} from '../../../../services/apiaryService';

const PAGE_SIZE = 50;

const STATUS_VARIANT = {
    PENDING: 'warning',
    APPROVED: 'success',
    REJECTED: 'danger',
    CANCELLED: 'secondary',
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN') : '—');
const formatAmount = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

const AdvanceRequestsTab = ({ isAdmin, onPendingCount }) => {
    const empIdFor = useEmployeeIds();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reasons, setReasons] = useState([]);
    const [selected, setSelected] = useState(null);
    const [actingId, setActingId] = useState(null);

    const [status, setStatus] = useState('PENDING');
    const [reason, setReason] = useState('all');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    const reasonLabel = useCallback(
        (key) => reasons.find((r) => r.key === key)?.label || key,
        [reasons]
    );

    useEffect(() => {
        getAdvanceReasons().then(setReasons);
    }, []);

    // Search waits for a short pause in typing before it hits the server.
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getAdvanceRequests({ status, reason, from, to, search, page, limit: PAGE_SIZE });
            setRows(res?.data || []);
            setTotal(res?.total || 0);
            if (onPendingCount) onPendingCount(res?.pendingCount || 0);
        } catch (err) {
            console.error('Error loading advance requests:', err);
            setError(err?.response?.data?.error || 'Could not load advance requests.');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [status, reason, from, to, search, page, onPendingCount]);

    useEffect(() => {
        load();
    }, [load]);

    const changeFilter = (setter) => (e) => {
        setter(e.target.value);
        setPage(1);
    };

    // Approval is limited to admins / field admins / super users, decided once by checkIsAdmin()
    // in the Hub (it reads every admin flag variant). Guard the handlers too, not just the buttons.
    const ensureCanDecide = () => {
        if (isAdmin) return true;
        toast.error('Only admins can approve or reject advance requests.');
        return false;
    };

    const handleApprove = async (row) => {
        if (!ensureCanDecide()) return;
        const result = await Swal.fire({
            title: 'Approve advance?',
            html: `Credit <b>${formatAmount(row.amount)}</b> to <b>${row.user_name || 'this user'}</b> as a wallet advance.`,
            input: 'text',
            inputPlaceholder: 'Comment (optional)',
            showCancelButton: true,
            confirmButtonText: 'Approve',
            confirmButtonColor: '#00b894',
        });
        if (!result.isConfirmed) return;
        setActingId(row._id);
        try {
            await approveAdvanceRequest(row._id, result.value);
            toast.success('Advance approved and added to the wallet');
            setSelected(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to approve advance request');
            await load();
        } finally {
            setActingId(null);
        }
    };

    const handleReject = async (row) => {
        if (!ensureCanDecide()) return;
        const result = await Swal.fire({
            title: 'Reject advance request?',
            input: 'textarea',
            inputPlaceholder: 'Reason for rejection (required)',
            inputValidator: (value) => (!value || !value.trim() ? 'A comment is required to reject' : null),
            showCancelButton: true,
            confirmButtonText: 'Reject',
            confirmButtonColor: '#d63031',
        });
        if (!result.isConfirmed) return;
        setActingId(row._id);
        try {
            await rejectAdvanceRequest(row._id, result.value.trim());
            toast.success('Advance request rejected');
            setSelected(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to reject advance request');
            await load();
        } finally {
            setActingId(null);
        }
    };

    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div style={{ padding: '10px 0' }}>
            <Row className="mb-3">
                <Col md={2} className="mb-2">
                    <Form.Label>Status</Form.Label>
                    <Form.Control as="select" value={status} onChange={changeFilter(setStatus)}>
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                        <option value="CANCELLED">Cancelled</option>
                        <option value="all">All</option>
                    </Form.Control>
                </Col>
                <Col md={3} className="mb-2">
                    <Form.Label>Reason</Form.Label>
                    <Form.Control as="select" value={reason} onChange={changeFilter(setReason)}>
                        <option value="all">All reasons</option>
                        {reasons.map((r) => (
                            <option key={r.key} value={r.key}>{r.label}</option>
                        ))}
                    </Form.Control>
                </Col>
                <Col md={2} className="mb-2">
                    <Form.Label>From</Form.Label>
                    <Form.Control type="date" value={from} onChange={changeFilter(setFrom)} />
                </Col>
                <Col md={2} className="mb-2">
                    <Form.Label>To</Form.Label>
                    <Form.Control type="date" value={to} onChange={changeFilter(setTo)} />
                </Col>
                <Col md={3} className="mb-2">
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Name, mobile or request no."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                </Col>
            </Row>

            {error && <div className="alert alert-danger">{error}</div>}

            <div style={{ overflowX: 'auto' }}>
                <Table hover size="sm" style={{ background: '#fff' }}>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Request</th>
                            <th>Requested by</th>
                            <th>Employee ID</th>
                            <th>Reason</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th>Reports to</th>
                            <th>Status</th>
                            <th style={{ width: 190 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={9} className="text-center text-muted py-4">Loading advance requests...</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={9} className="text-center text-muted py-4">No advance requests found.</td></tr>
                        ) : (
                            rows.map((row) => (
                                <tr key={row._id} style={{ cursor: 'pointer' }} onClick={() => setSelected(row)}>
                                    <td>{formatDate(row.created_at)}</td>
                                    <td>{row.request_id_display}</td>
                                    <td>
                                        {row.user_name || '—'}
                                        {row.user_mobile ? <div className="text-muted" style={{ fontSize: 12 }}>{row.user_mobile}</div> : null}
                                    </td>
                                    <td>
                                        {empIdFor(row.user_id)
                                            ? <span style={{ fontWeight: 700, fontSize: 11, fontFamily: 'monospace', color: '#2563eb', background: '#eff6ff', padding: '3px 8px', borderRadius: 4, border: '1px solid #bfdbfe' }}>{empIdFor(row.user_id)}</span>
                                            : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                                    </td>
                                    <td>
                                        {reasonLabel(row.reason)}
                                        {row.reason_details ? (
                                            <div className="text-muted" style={{ fontSize: 12, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {row.reason_details}
                                            </div>
                                        ) : null}
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(row.amount)}</td>
                                    <td>{row.reporting_to_name || '—'}</td>
                                    <td><Badge variant={STATUS_VARIANT[row.approval_status] || 'secondary'}>{row.approval_status}</Badge></td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        {isAdmin && row.approval_status === 'PENDING' ? (
                                            <>
                                                <Button size="sm" variant="success" className="mr-2" disabled={actingId === row._id} onClick={() => handleApprove(row)}>Approve</Button>
                                                <Button size="sm" variant="outline-danger" disabled={actingId === row._id} onClick={() => handleReject(row)}>Reject</Button>
                                            </>
                                        ) : null}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="text-muted">{total} request{total === 1 ? '' : 's'}</span>
                <div>
                    <Button size="sm" variant="outline-secondary" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                    <span className="mx-3">Page {page} of {pageCount}</span>
                    <Button size="sm" variant="outline-secondary" disabled={page >= pageCount || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
            </div>

            <Modal show={!!selected} onHide={() => setSelected(null)} centered>
                {selected && (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>{selected.request_id_display}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p><b>Requested by:</b> {selected.user_name || '—'} {empIdFor(selected.user_id) ? `(${empIdFor(selected.user_id)})` : ''} {selected.user_mobile ? `- ${selected.user_mobile}` : ''}</p>
                            <p><b>Amount:</b> {formatAmount(selected.amount)}</p>
                            <p><b>Reason:</b> {reasonLabel(selected.reason)}</p>
                            {selected.reason_details ? <p><b>Details:</b> {selected.reason_details}</p> : null}
                            <p><b>Reports to:</b> {selected.reporting_to_name || '—'}</p>
                            <p><b>Requested on:</b> {formatDate(selected.created_at)}</p>
                            {selected.needed_by ? <p><b>Needed by:</b> {formatDate(selected.needed_by)}</p> : null}
                            <p><b>Status:</b> <Badge variant={STATUS_VARIANT[selected.approval_status] || 'secondary'}>{selected.approval_status}</Badge></p>
                            {selected.approved_by_name ? (
                                <p>
                                    <b>{selected.approval_status === 'REJECTED' ? 'Rejected' : 'Approved'} by:</b> {selected.approved_by_name}
                                    {selected.approved_via === 'ADMIN_PORTAL' ? ' (admin portal)' : selected.approved_via === 'FIELD_VIEW' ? ' (Field View app)' : ''}
                                    {selected.approval_date ? ` on ${formatDate(selected.approval_date)}` : ''}
                                </p>
                            ) : null}
                            {selected.approval_comment ? <p><b>Comment:</b> {selected.approval_comment}</p> : null}
                            {selected.advance_id ? <p><b>Ledger advance:</b> created (see Issued Advances)</p> : null}
                        </Modal.Body>
                        <Modal.Footer>
                            {isAdmin && selected.approval_status === 'PENDING' ? (
                                <>
                                    <Button variant="outline-danger" disabled={actingId === selected._id} onClick={() => handleReject(selected)}>Reject</Button>
                                    <Button variant="success" disabled={actingId === selected._id} onClick={() => handleApprove(selected)}>Approve</Button>
                                </>
                            ) : (
                                <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
                            )}
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default AdvanceRequestsTab;
